import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Spinner,
} from "reactstrap";

import type { OrdersOptionsActionPreference } from "../../../../helpers/ordersListContext";

type OrdersOptionsMenuProps = {
  guideUrl: string;
  exportingCsv: boolean;
  exportDisabled?: boolean;
  lastAction: OrdersOptionsActionPreference | null;
  onRememberAction: (action: OrdersOptionsActionPreference) => void;
  onExportCsv: () => void;
};

const OrdersOptionsMenu = ({
  guideUrl,
  exportingCsv,
  exportDisabled = false,
  lastAction,
  onRememberAction,
  onExportCsv,
}: OrdersOptionsMenuProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const recentBadge = (action: OrdersOptionsActionPreference) =>
    lastAction === action ? (
      <span className="orders-options-menu__recent text-muted small ms-2">
        {t("studio.orders.options.recent")}
      </span>
    ) : null;

  const rememberAndRun = (action: OrdersOptionsActionPreference, run: () => void) => {
    onRememberAction(action);
    run();
  };

  const runExport = () => {
    if (exportDisabled || exportingCsv) {
      return;
    }

    rememberAndRun("export_csv", onExportCsv);
  };

  const quickLabel =
    lastAction === "export_csv"
      ? t("studio.orders.actions.exportCsv")
      : lastAction === "guide"
        ? t("help.articleLink")
        : null;

  const quickIcon =
    lastAction === "export_csv" ? (
      exportingCsv ? (
        <Spinner size="sm" className="me-1" />
      ) : (
        <i className="ri-download-2-line align-middle me-1" aria-hidden="true" />
      )
    ) : lastAction === "guide" ? (
      <i className="ri-book-open-line align-middle me-1" aria-hidden="true" />
    ) : null;

  return (
    <div className="orders-options-menu d-flex align-items-center gap-1">
      {lastAction === "export_csv" ? (
        <Button
          color="secondary"
          outline
          size="sm"
          type="button"
          className="orders-options-menu__quick bg-white text-body border-secondary-subtle"
          disabled={exportDisabled || exportingCsv}
          onClick={runExport}
          title={quickLabel ?? undefined}
        >
          {quickIcon}
          <span className="orders-options-menu__quick-label">{quickLabel}</span>
        </Button>
      ) : null}
      {lastAction === "guide" ? (
        <Button
          color="secondary"
          outline
          size="sm"
          tag="a"
          href={guideUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="orders-options-menu__quick bg-white text-body border-secondary-subtle"
          onClick={() => onRememberAction("guide")}
          title={quickLabel ?? undefined}
        >
          {quickIcon}
          <span className="orders-options-menu__quick-label">{quickLabel}</span>
        </Button>
      ) : null}
      <Dropdown
        isOpen={open}
        toggle={() => setOpen((current) => !current)}
        className="orders-options-menu__dropdown"
      >
        <DropdownToggle
          color="secondary"
          outline
          size="sm"
          className="orders-options-menu__toggle bg-white text-body border-secondary-subtle"
          caret
        >
          <i className="ri-settings-3-line align-middle me-1" aria-hidden="true" />
          {t("studio.orders.options.label")}
        </DropdownToggle>
        <DropdownMenu end container="body" positionFixed>
          <DropdownItem
            disabled={exportDisabled || exportingCsv}
            onClick={() => {
              setOpen(false);
              runExport();
            }}
          >
            {exportingCsv ? (
              <Spinner size="sm" className="me-1" />
            ) : (
              <i className="ri-download-2-line align-middle me-1" aria-hidden="true" />
            )}
            {t("studio.orders.actions.exportCsv")}
            {recentBadge("export_csv")}
          </DropdownItem>
          <DropdownItem
            tag="a"
            href={guideUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              setOpen(false);
              onRememberAction("guide");
            }}
          >
            <i className="ri-book-open-line align-middle me-1" aria-hidden="true" />
            {t("help.articleLink")}
            {recentBadge("guide")}
          </DropdownItem>
        </DropdownMenu>
      </Dropdown>
    </div>
  );
};

export default OrdersOptionsMenu;
